import { Alert, Button, Stack, Text, useNotify } from '@hotjar/hotsauce';
import { useVideoService } from '@unmoderated/video-service';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import WebsiteIllustration from '../../../assets/images/website.svg';
import { useThankYouDetailsPrefetch } from '../../api';
import { WithErrorBoundary } from '../../components/ErrorBoundary/ErrorBoundary';
import { FullScreen } from '../../components/FullScreen/FullScreen';
import { openStopTestModal } from '../../components/Modals/StopTestModal';
import { routes } from '../../routes';
import { getAnalytics } from '../../utils/observability';
import { IconContainer, IllustrationContainer, TipContainer } from './PreviewScreen.styles';
import { checkRequiredReferrerParams, openTestSite } from './PreviewScreen.utils';
import { useProjectParams } from '../../utils/hooks/useProjectParams';
import { useClientScriptControl } from '../../utils/hooks/useClientScriptControl';

export function PreviewScreenComponent() {
  const analytics = getAnalytics();
  const navigate = useNavigate();
  const { getParam } = useProjectParams();
  const testUrl = getParam('website_testing_url');
  const shareId = getParam('share_id');

  const { showToast } = useNotify();

  useThankYouDetailsPrefetch({ shareId, auth: getParam('auth') });

  const { t } = useTranslation('user-test', { keyPrefix: 'preview' });

  const { isSharingScreen, startRecording, stopService, isRecording } = useVideoService();
  const [startLoading, setStartLoading] = useState(false);
  const [isTestStarted, setIsTestStarted] = useState(false);
  const [isTestFinished, setIsTestFinished] = useState(false);

  const isPreview = !!getParam('is_preview');
  const [paramsValidityChecked, setParamsValidityChecked] = useState<boolean | undefined>();
  const isParamsInValid = paramsValidityChecked === false;

  const onFinished = useCallback(async () => {
    setIsTestFinished(true);

    navigate(routes.THANK_YOU.getPath(window.location.search));
    // stopping recording on finish with timeout to include the thank-you screen
    setTimeout(() => {
      stopService(isPreview);
    }, 3000);
  }, [isPreview, navigate, stopService]);

  useClientScriptControl({ onFinished });

  useEffect(() => {
    checkRequiredReferrerParams().then((isValid) => {
      setParamsValidityChecked(isValid);
    });
  }, []);

  useEffect(() => {
    if (isParamsInValid) {
      console.error('Invalid URL, missing referrer parameters.', window.location.search);
    }
  }, [isParamsInValid]);

  const handleStartTest = (event: MouseEvent) => {
    event.preventDefault();

    if (!testUrl) {
      return;
    }

    analytics.track('Element Clicked', {
      element_name: 'Get started button',
      ui_position: 'ut_lobby.preview_screen.start_button',
    });

    setStartLoading(true);

    const start = isPreview ? () => Promise.resolve() : startRecording;

    start()
      .then(() => {
        setStartLoading(false);
        setIsTestStarted(true);
        return openTestSite(testUrl);
      })
      .catch((error) => {
        console.error('Error starting recording:', error);
      })
      .finally(() => {
        setStartLoading(false);
      });
  };

  const stopTest = useCallback(async () => {
    setIsTestStarted(false);
    await stopService(isPreview);
    navigate(routes.PRE_CHECK.getPath(window.location.search));
  }, [isPreview, navigate, stopService]);

  //Sharing stopped -> stopping Test
  useEffect(() => {
    if (!isSharingScreen && isTestStarted && !isTestFinished && !isPreview) {
      showToast({ text: t('alerts.user-aborted'), kind: 'danger' });
      stopTest().catch();
    }
  }, [isPreview, isSharingScreen, isTestFinished, isTestStarted, showToast, stopTest, t]);

  const handleStopTest = async () => {
    analytics.track('Element Clicked', {
      element_name: 'Stop test button',
      ui_position: 'ut_lobby.preview_screen.stop_button',
    });

    await openStopTestModal({
      title: t('stop-test-modal.title'),
      description: t('stop-test-modal.description'),
      confirmButtonText: t('stop-test-modal.confirm'),
      confirmButtonKind: 'danger',
    });

    await stopTest();
  };

  const canStartTest =
    testUrl && (isPreview || (!isParamsInValid && isSharingScreen && !isRecording));

  return (
    <FullScreen
      leftSide={
        <Stack direction="column" spacing="x6">
          {isParamsInValid && <Alert kind="danger">{t('alerts.referrer-invalid')}</Alert>}
          <Stack direction="column">
            <Text textType="header3" as="h3">
              {t('title')}
            </Text>
            <Text textType="body1">{t('description')}</Text>
          </Stack>
          <Stack direction="column" spacing="x6">
            <TipContainer>
              <IconContainer>
                <Text textType="header5" as="h5">
                  1
                </Text>
              </IconContainer>
              <Stack direction="column">
                <Text textType="subtitle1">{t('instructions.tabs.title')}</Text>
                <Text>{t('instructions.tabs.description')}</Text>
              </Stack>
            </TipContainer>
            <TipContainer>
              <IconContainer>
                <Text textType="header5" as="h5">
                  2
                </Text>
              </IconContainer>
              <Stack direction="column">
                <Text textType="subtitle1">{t('instructions.test.title')}</Text>
                <Text>{t('instructions.test.description')}</Text>
              </Stack>
            </TipContainer>
            <TipContainer>
              <IconContainer>
                <Text textType="header5" as="h5">
                  3
                </Text>
              </IconContainer>
              <Stack direction="column">
                <Text textType="subtitle1">{t('instructions.safety.title')}</Text>
                <Text>{t('instructions.safety.description')}</Text>
              </Stack>
            </TipContainer>
            <TipContainer>
              <IconContainer>
                <Text textType="header5" as="h5">
                  4
                </Text>
              </IconContainer>
              <Stack direction="column">
                <Text textType="subtitle1">{t('instructions.vocal.title')}</Text>
                <Text>{t('instructions.vocal.description')}</Text>
              </Stack>
            </TipContainer>
          </Stack>
        </Stack>
      }
      hideOnSmallScreen="right"
      rightSide={
        <IllustrationContainer>
          <img src={WebsiteIllustration} alt="" />
        </IllustrationContainer>
      }
      footer={
        <>
          {!isPreview && (
            <Button
              kind="ghost"
              isDisabled={!isTestStarted}
              onClick={handleStopTest}
              type="button"
              className="secondary"
            >
              Stop test
            </Button>
          )}
          {canStartTest && !isTestStarted && (
            <Button
              icon="open_new_window"
              iconPosition="right"
              onClick={handleStartTest}
              isLoading={startLoading}
              className="start-button"
            >
              Get started
            </Button>
          )}
        </>
      }
    />
  );
}

export const PreviewScreen = WithErrorBoundary(PreviewScreenComponent, 'PreviewScreen');
